import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Form,
  Input,
  Table,
  Button,
  message,
  Tag,
  Drawer,
  Modal,
  Tooltip,
  Grid,
  Typography,
} from 'antd';
import {
  EditOutlined,
  LockOutlined,
  UnlockOutlined,
  DeleteOutlined,
  SearchOutlined,
} from '@ant-design/icons';

import {
  useCreateTeamUser,
  useDeleteTeamUser,
  useGetAllTeamUsers,
  useUpdateTeamUser,
} from './Hooks';
import { useAuthStore } from '../../store/useAuthStore';
import useDebounce from '../../utils/debounce';
import { validations } from '../../utils/validation';

const { Title } = Typography;
const { useBreakpoint } = Grid;

export default function TeamManagement() {
  const { t } = useTranslation('teamuser');
  const { user } = useAuthStore();
  const currentUserEmail = user?.email;

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [editUser, setEditUser] = useState(null);
  const [loadingUserId, setLoadingUserId] = useState(null);
  const [form] = Form.useForm();
  const [editForm] = Form.useForm();
  const [search, setSearch] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const debouncedSearch = useDebounce(search, 500);
  const [loadingAction, setLoadingAction] = useState(null);

  const screens = useBreakpoint();
  const isMobile = !screens.md;

  const { data, isLoading, refetch } = useGetAllTeamUsers({
    offset: currentPage,
    limit: pageSize,
    search: debouncedSearch,
  });

  const users = data?.paginatedData || [];
  const totalUsers = data?.totalCount || 0;

  const createUserMutation = useCreateTeamUser({
    onSuccess: () => {
      message.success(t('userCreated'));
      refetch();
      setIsModalOpen(false);
      form.resetFields();
      setLoadingUserId(null);
      setLoadingAction(null);
    },
    onError: (error) => {
      message.error(error?.response?.data?.message || t('createFailed'));
      setLoadingUserId(null);
      setLoadingAction(null);
    },
  });

  const updateUserMutation = useUpdateTeamUser({
    onSuccess: () => {
      message.success(t('userUpdated'));
      refetch();
      setIsEditModalOpen(false);
      setEditUser(null);
      editForm.resetFields();
      setLoadingUserId(null);
      setLoadingAction(null);
    },
    onError: (error) => {
      message.error(error?.response?.data?.message || t('updateFailed'));
      setLoadingUserId(null);
      setLoadingAction(null);
    },
  });

  const deleteUserMutation = useDeleteTeamUser({
    onSuccess: () => {
      message.success(t('userDeleted'));
      refetch();
      setLoadingUserId(null);
      setLoadingAction(null);
    },
    onError: (error) => {
      message.error(error?.response?.data?.message || t('deleteFailed'));
      setLoadingUserId(null);
      setLoadingAction(null);
    },
  });

  const handleAddUser = (values) => {
    const { phone, phone_code, ...rest } = values;
    createUserMutation.mutate({
      ...rest,
      phone,
      phone_code: phone_code || '90',
      role: 'ADMIN',
    });
  };

  const handleUpdateUser = (values) => {
    // eslint-disable-next-line no-unused-vars
    const { phone, phone_code, email, role, ...updatedValues } = values;
    setLoadingUserId(editUser._id);
    setLoadingAction('edit');
    updateUserMutation.mutate({
      data: {
        ...updatedValues,
        phone,
        phone_code: phone_code || '90',
        role: 'ADMIN',
      },
      id: editUser._id,
    });
  };

  const handleEditUser = (record) => {
    setEditUser(record);
    editForm.setFieldsValue({
      fname: record.fname,
      lname: record.lname,
      email: record.email,
      phone: record.phone,
      phone_code: record.phone_code || '90',
      status: record.status,
    });
    setIsEditModalOpen(true);
  };

  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [blockUnblockModalOpen, setBlockUnblockModalOpen] = useState(false);
  const [rfidToBlockUnblock, setRfidToBlockUnblock] = useState(null);
  const [userToDelete, setUserToDelete] = useState(null);

  const confirmBlockUnblockUser = (block) => {
    setLoadingUserId(rfidToBlockUnblock._id);
    setLoadingAction('block');
    updateUserMutation.mutate({
      id: rfidToBlockUnblock._id,
      data: {
        fname: rfidToBlockUnblock.fname,
        lname: rfidToBlockUnblock.lname,
        status: block ? 'BLOCKED' : 'ACTIVE',
        role: 'ADMIN',
      },
    });
    setBlockUnblockModalOpen(false);
  };

  const confirmDeleteUser = () => {
    setLoadingUserId(userToDelete._id);
    setLoadingAction('delete');
    deleteUserMutation.mutate(userToDelete._id);
    setDeleteModalOpen(false);
  };

  const columns = [
    {
      title: t('firstName'),
      dataIndex: 'fname',
      key: 'fname',
      align: 'center',
    },
    {
      title: t('lastName'),
      dataIndex: 'lname',
      key: 'lname',
      align: 'center',
    },
    {
      title: t('email'),
      dataIndex: 'email',
      key: 'email',
      align: 'center',
      className: 'whitespace-nowrap',
      width: 350,
      render: (text) => (
        <Tooltip placement="topLeft" title={text}>
          {text}
        </Tooltip>
      ),
    },
    {
      title: t('role'),
      dataIndex: 'role',
      key: 'role',
      align: 'center',
      render: (role) => (
        <Tag color="blue" variant="outlined">
          {role}
        </Tag>
      ),
    },
    {
      title: t('status'),
      dataIndex: 'status',
      key: 'status',
      align: 'center',
      render: (status) => (
        <Tag color={status === 'ACTIVE' ? 'green' : 'red'} variant="outlined">
          {t(status)}
        </Tag>
      ),
    },
    {
      title: t('actions'),
      key: 'actions',
      align: 'center',
      render: (_, record) => {
        const isCurrentUser = record.email === currentUserEmail;
        const isBlocked = record.status === 'BLOCKED';
        return (
          <div className="flex space-x-2">
            <Tooltip title={t('editUser')}>
              <Button
                disabled={loadingUserId === record._id && loadingAction !== 'edit'}
                icon={<EditOutlined />}
                loading={loadingUserId === record._id && loadingAction === 'edit'}
                onClick={() => handleEditUser(record)}
              />
            </Tooltip>
            <Tooltip title={isBlocked ? t('userUnblockTitle') : t('userBlockTitle')}>
              <Button
                disabled={
                  isCurrentUser ||
                  (loadingUserId === record._id && loadingAction !== 'block')
                }
                icon={isBlocked ? <UnlockOutlined /> : <LockOutlined />}
                loading={loadingUserId === record._id && loadingAction === 'block'}
                onClick={() => {
                  setRfidToBlockUnblock(record);
                  setBlockUnblockModalOpen(true);
                }}
              />
            </Tooltip>
            <Tooltip title={t('userDeleteTitle')}>
              <Button
                danger
                disabled={
                  isCurrentUser ||
                  (loadingUserId === record._id && loadingAction !== 'delete')
                }
                icon={<DeleteOutlined />}
                loading={loadingUserId === record._id && loadingAction === 'delete'}
                onClick={() => {
                  setUserToDelete(record);
                  setDeleteModalOpen(true);
                }}
              />
            </Tooltip>
          </div>
        );
      },
    },
  ];

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center mb-4 gap-4">
        <Input
          style={{ width: 300 }}
          placeholder={t('searchPlaceholder')}
          prefix={<SearchOutlined />}
          value={search}
          allowClear
          onChange={(e) => {
            setSearch(e.target.value);
            setCurrentPage(1);
          }}
        />
        <Button type="primary" onClick={() => setIsModalOpen(true)}>
          {t('addUser')}
        </Button>
      </div>
      <Table
        columns={columns}
        dataSource={users}
        loading={isLoading}
        variant="outlined"
        size="small"
        tableLayout="fixed"
        scroll={{ x: 'max-content', y: 60 * 8.3 }}
        pagination={{
          current: currentPage,
          pageSize,
          size: 'default',
          total: totalUsers,
          onChange: (page, size) => {
            setCurrentPage(page);
            setPageSize(size);
          },
          showSizeChanger: false,
          position: ['bottomCenter'],
        }}
        rowKey="_id"
      />
      {isModalOpen && (
        <Drawer
          maskClosable={false}
          title={<Title level={4} className="mb-0!">{t('createUser')}</Title>}
          open={isModalOpen}
          onClose={() => {
            setIsModalOpen(false);
            form.resetFields();
          }}
          width={isMobile ? '100%' : 600}
          destroyOnHidden
        >
          <Form form={form} layout="vertical" onFinish={handleAddUser}>
            <Form.Item
              label={t('firstName')}
              name="fname"
              rules={[
                { required: true, message: t('validation.firstNameRequired') },
                { max: 30, message: t('validation.max30Characters') },
                { whitespace: true, message: t('validation.noWhitespaceAllowed') },
              ]}
            >
              <Input placeholder={t('enterFirstName')} />
            </Form.Item>
            <Form.Item
              label={t('lastName')}
              name="lname"
              rules={[
                { required: true, message: t('validation.lastNameRequired') },
                { max: 30, message: t('validation.max30Characters') },
                { whitespace: true, message: t('validation.noWhitespaceAllowed') },
              ]}
            >
              <Input placeholder={t('enterLastName')} />
            </Form.Item>
            <Form.Item
              label={t('email')}
              name="email"
              rules={[
                { required: true, message: t('validation.emailRequired') },
                { type: 'email', message: t('validation.invalidEmail') },
                { max: 100, message: t('validation.max100Characters') },
              ]}
            >
              <Input placeholder={t('enterEmail')} />
            </Form.Item>
            <Form.Item
              label={t('phoneNumber')}
              name="phone"
              rules={[validations.phoneNumber(t('validation.invalidTurkishMobile'))]}
            >
              <Input placeholder={t('enterPhoneNumber')} prefix="+90" />
            </Form.Item>
            <div className="flex justify-start space-x-2">
              <Button htmlType="submit" loading={createUserMutation.isPending} type="primary">
                {t('submit')}
              </Button>
              <Button
                onClick={() => {
                  setIsModalOpen(false);
                  form.resetFields();
                }}
              >
                {t('cancel')}
              </Button>
            </div>
          </Form>
        </Drawer>
      )}
      <Drawer
        maskClosable={false}
        title={<Title level={4} className="mb-0!">{t('editUser')}</Title>}
        open={isEditModalOpen}
        destroyOnHidden
        onClose={() => {
          setIsEditModalOpen(false);
          editForm.resetFields();
          setEditUser(null);
        }}
        width={isMobile ? '100%' : 600}
      >
        <Form form={editForm} layout="vertical" onFinish={handleUpdateUser}>
          <Form.Item
            label={t('firstName')}
            name="fname"
            rules={[
              { required: true, message: t('validation.firstNameRequired') },
              { max: 30, message: t('validation.max30Characters') },
              { whitespace: true, message: t('validation.noWhitespaceAllowed') },
            ]}
          >
            <Input placeholder={t('enterFirstName')} />
          </Form.Item>
          <Form.Item
            label={t('lastName')}
            name="lname"
            rules={[
              { required: true, message: t('validation.lastNameRequired') },
              { max: 30, message: t('validation.max30Characters') },
              { whitespace: true, message: t('validation.noWhitespaceAllowed') },
            ]}
          >
            <Input placeholder={t('enterLastName')} />
          </Form.Item>
          <Form.Item label={t('email')} name="email">
            <Input disabled />
          </Form.Item>
          <Form.Item
            label={t('phoneNumber')}
            name="phone"
            rules={[validations.phoneNumber(t('validation.invalidTurkishMobile'))]}
          >
            <Input placeholder={t('enterPhoneNumber')} addonBefore="+90" />
            <Form.Item noStyle name="phone_code" initialValue="90">
              <input type="hidden" />
            </Form.Item>
          </Form.Item>
          <div className="flex justify-start space-x-2">
            <Button
              htmlType="submit"
              loading={loadingUserId === editUser?._id && loadingAction === 'edit'}
              type="primary"
            >
              {t('submit')}
            </Button>
            <Button
              onClick={() => {
                setIsEditModalOpen(false);
                editForm.resetFields();
                setEditUser(null);
              }}
            >
              {t('cancel')}
            </Button>
          </div>
        </Form>
      </Drawer>

      <Modal
        title={t('confirmDelete')}
        open={deleteModalOpen}
        onOk={confirmDeleteUser}
        onCancel={() => setDeleteModalOpen(false)}
        okText={t('delete')}
        okButtonProps={{ danger: true }}
      >
        <p>{t('deleteUserConfirm')}</p>
      </Modal>

      <Modal
        title={
          rfidToBlockUnblock?.status === 'BLOCKED'
            ? t('userUnblockTitle')
            : t('userBlockTitle')
        }
        open={blockUnblockModalOpen}
        onOk={() =>
          confirmBlockUnblockUser(rfidToBlockUnblock?.status !== 'BLOCKED')
        }
        onCancel={() => setBlockUnblockModalOpen(false)}
      >
        <p>
          {rfidToBlockUnblock?.status === 'BLOCKED'
            ? t('unblockUserConfirm')
            : t('blockUserConfirm')}
        </p>
      </Modal>
    </div>
  );
}
